import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useNoteUpdatePinMutation } from 'api';
import { AllEntityTypes, Note as NoteType, TypeOfEntityType } from 'models/ComunicationHub';
import { ADMIN_ROUTES } from 'constants/routes';
import { CommunicationHubEntityType } from 'components/common/communicationHub/index';

interface IRelations {
  id: string;
  uuid: string;
  entity: string;
  label: string;
  name: string;
}

interface InputItem {
  id: string;
  uuid: string;
  name: string;
  entity: string;
  label: string;
}

interface RelationsOutput {
  [relationType: string]: InputItem[];
}

const parseRelations = (relationsOutput: RelationsOutput, entityToSkip: string): InputItem[] => {
  const inputItems: InputItem[] = [];
  Object.entries(relationsOutput).forEach(([relationType, values]) => {
    if (relationType !== entityToSkip) {
      values.forEach((value) => {
        inputItems.push({ ...value, entity: relationType, label: AllEntityTypes[relationType]?.label });
      });
    }
  });
  return inputItems;
};
export const useNote = (props: { note: NoteType; entity: CommunicationHubEntityType }) => {
  const { note, entity } = props;
  const userId = JSON.parse(localStorage.getItem('userId') as string);
  const relations: IRelations[] = useMemo(() => parseRelations(note.relations, entity), [note.relations, entity]);
  const { id } = useParams();
  const navigate = useNavigate();
  const { handleError, handleSuccess } = useNotificationMessages();

  const [updatePin] = useNoteUpdatePinMutation();
  const onPinChange = useCallback(
    async (noteId: string) => {
      try {
        const { data, error } = await updatePin({
          noteId,
          entityName: entity,
          entityId: id,
        });
        if (data?.success) {
          handleSuccess(`Note successfully ${note.pinned ? 'unpinned' : 'pinned'}.`);
        } else {
          handleError(error);
        }
      } catch (err) {
        handleError(err);
      }
    },
    [id, note, updatePin, entity, handleSuccess, handleError]
  );

  const onRouteChange = useCallback(
    (entityName: TypeOfEntityType, id: string) => {
      switch (entityName) {
        case 'SUPPLIER':
          navigate(`${ADMIN_ROUTES.suppliers.view}/${id}/general`);
          return;
        case 'BRAND':
          navigate(`${ADMIN_ROUTES.brand.view}/${id}/management`);
          return;
        case 'CLIENT':
          navigate(`${ADMIN_ROUTES.client}/${id}/general`);
          return;
        case 'LEAD':
          navigate(`${ADMIN_ROUTES.leads.view}/${id}/general`);
          return;
        case 'DEAL':
          navigate(`${ADMIN_ROUTES.deals.view}/${id}/purchase-request`);
          return;
        case 'PURCHASE_REQUEST':
          navigate(`${ADMIN_ROUTES.purchaseRequest.view}/${id}/general`);
          return;
        case 'PURCHASE_ORDER':
          navigate(`${ADMIN_ROUTES.purchaseOrder.view}/${id}/general`);
          return;
        default:
          return;
      }
    },
    [navigate]
  );

  return {
    onRouteChange,
    onPinChange,
    userId,
    relations,
  };
};
